import React, { Fragment, useState, useContext } from "react";
import { Box } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { TimePicker, DatePicker } from "@mui/x-date-pickers";

import dayjs from "dayjs";

import { CurrentUserContext } from "../../context/CurrentUserContext";
import { StaticWindow } from "../../components/ui/windows";
import { CancelConfirm } from "../../components/ui/buttons";
import { TextInput } from "../../components/inputs";
import { WorkFunctionInput } from "../work-functions/WorkFunctionInput";
import ConfirmationDialog from "../../components/boilerplate/ConfirmationDialog";

import * as api from "../../services/timeAPI";
import { DefinitionsContext } from "../../context/DefinitionsContext";

import enGB from "date-fns/locale/en-GB";
import de from "date-fns/locale/de";
import fr from "date-fns/locale/fr";

// map languageId -> date-fns locale
function getDateFnsLocale(languageId) {
  switch (languageId) {
    case 1: // anglais
      return enGB;
    case 2: // allemand
      return de;
    case 3: // français
      return fr;
    default:
      return enGB;
  }
}

function TimeRequestCreator({ isOpen, onClose, onUpdate }) {
  const { translationList, workFunctions } = useContext(DefinitionsContext);
  const { currentUser } = useContext(CurrentUserContext);

  const dateFnsLocale = getDateFnsLocale(currentUser.languageId);

  // TODO: Change to direction id
  const workFunDefault = 14;

  // on garde dayjs pour la logique interne et l'API
  const [date, setDate] = useState(dayjs());
  const [startTime, setStartTime] = useState(dayjs().add(-1, "hour"));
  const [endTime, setEndTime] = useState(dayjs());
  const [workFunctionId, setWorkFunctionId] = useState(workFunDefault);
  const [description, setDescription] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [modalConfirmation, setModalConfirmation] = useState(false);

  function handleSubmit() {
    // TODO: check if data is complete
    api.requestTime(
        date,
        startTime,
        endTime,
        currentUser.id,
        "direction",
        description,
        workFunctionId,
        onUpdate
    );
    handleClose();
  }

  function funIdToName(id) {
    return workFunctions.find((el) => el.id === id)?.name;
  }

  function handleWorkFunctionChange(workFunctionName) {
    const workFunId = workFunctions.find(
        (el) => el.name === workFunctionName
    ).id;
    setWorkFunctionId(workFunId);
  }

  function resetState() {
    setDate(dayjs());
    setStartTime(dayjs().add(-1, "hour"));
    setEndTime(dayjs());
    setWorkFunctionId(workFunDefault);
    setDescription("");
    setIsValid(true);
  }

  function handleClose() {
    resetState();
    onClose();
  }

  function handleValidChange(label, isFieldValid) {
    setIsValid(isFieldValid);
  }

  // AdapterDateFns attend des Date natifs, on convertit donc les dayjs
  const dateValue = date ? date.toDate() : null;
  const startTimeValue = startTime ? startTime.toDate() : null;
  const endTimeValue = endTime ? endTime.toDate() : null;

  return (
      <Fragment>
        <ConfirmationDialog
            isOpen={modalConfirmation}
            text={translationList.sureSubmitTimeRequest}
            onClose={() => setModalConfirmation(false)}
            onConfirm={handleSubmit}
        />
        <StaticWindow
            isOpen={isOpen}
            onClose={handleClose}
            title={translationList.logTime}
        >
          <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
              }}
          >
            <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={dateFnsLocale}
            >
              <DatePicker
                  format={"dd.MM.yyyy"}
                  value={dateValue}
                  onChange={(newDate) => setDate(newDate ? dayjs(newDate) : null)}
              />
              <Box
                  sx={{
                    display: "flex",
                    width: "100%",
                    gap: 1,
                  }}
              >
                <TimePicker
                    ampm={false}
                    label={translationList.begin}
                    value={startTimeValue}
                    onChange={(newTime) =>
                        setStartTime(newTime ? dayjs(newTime) : null)
                    }
                    sx={{ width: "100%" }}
                />
                <TimePicker
                    ampm={false}
                    label={translationList.end}
                    value={endTimeValue}
                    onChange={(newTime) =>
                        setEndTime(newTime ? dayjs(newTime) : null)
                    }
                    sx={{ width: "100%" }}
                />
              </Box>
              <WorkFunctionInput
                  value={funIdToName(workFunctionId)}
                  onChange={handleWorkFunctionChange}
              />
              <TextInput
                  isRequired
                  fullWidth
                  multiline
                  rows={4}
                  label={translationList.description}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onValidChange={handleValidChange}
                  max={1000}
                  variant="outlined"
              />
            </LocalizationProvider>
          </Box>
          <CancelConfirm
              confirmLabel={translationList.submit}
              cancelLabel={translationList.cancel}
              disabled={!isValid}
              onCancel={handleClose}
              onConfirm={() => setModalConfirmation(true)}
          />
        </StaticWindow>
      </Fragment>
  );
}

export { TimeRequestCreator };
export default TimeRequestCreator;
